/**
 * One summary line prepended to a table_html reference so the model gets the table's
 * shape stated explicitly rather than having to infer it from raw markup, e.g.:
 * `table: 12 rows x 8 cols | header row 1: Visit | Dose (mg) | N | Mean`
 *
 * Why bother precomputing this - the HTML already contains it: reconstructing a 2D
 * grid (which cells share a visual row/column, how far a merge spans) from a
 * flattened token stream is exactly the spatial reasoning LLMs are weak at, not
 * something reading the markup solves for them. RealHiTBench's TreeThinker method
 * measured this directly - stating hierarchical table structure up front (rather
 * than switching table format) raised GPT-4o's numerical-reasoning F1 from 36.68 to
 * 49.35, a bigger swing than any serialization-format change tested. Our code has
 * the DOM already parsed, so computing rows/cols/merges here costs nothing; asking
 * the model to re-derive them from raw markup is the expensive, error-prone path.
 */
export function describeTableStructure(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const table = doc.querySelector('table')
  if (!table) return ''

  const rows = [...table.querySelectorAll('tr')]
  const colCount = rows.reduce(
    (max, row) =>
      Math.max(
        max,
        [...row.querySelectorAll('td, th')].reduce(
          (sum, cell) => sum + (Number(cell.getAttribute('colspan')) || 1),
          0
        )
      ),
    0
  )

  const parts = [`table: ${rows.length} rows x ${colCount} cols`]

  const headerText = describeHeaderRow(rows[0])
  if (headerText) parts.push(`header row 1: ${headerText}`)

  const mergedText = describeMergedCells(rows)
  if (mergedText) parts.push(`merged: ${mergedText}`)

  return parts.join('; ')
}

function describeHeaderRow(firstRow: Element | undefined): string | null {
  if (!firstRow) return null

  const cells = [...firstRow.querySelectorAll('td, th')]
  if (cells.length === 0) return null

  // A row only counts as a header when it's explicitly marked as one - <th> cells,
  // or a <thead> wrapper even when that wrapper uses plain <td> cells - not just
  // because it happens to be first, so an ordinary data row (slice 2a) stays plain.
  const isHeaderRow =
    firstRow.querySelectorAll('th').length > 0 || firstRow.closest('thead') !== null
  if (!isHeaderRow) return null

  return cells.map((cell) => cell.textContent?.trim() ?? '').join(' | ')
}

/**
 * Each merged cell reported as `rXcY spans N cols|rows`, using 1-based physical
 * position - the Nth cell actually present in the Nth row - so a cell is identifiable
 * unambiguously even alongside other merges in the same table.
 */
function describeMergedCells(rows: Element[]): string | null {
  const descriptions: string[] = []

  rows.forEach((row, rowIndex) => {
    const cells = [...row.querySelectorAll('td, th')]
    cells.forEach((cell, colIndex) => {
      const colspan = Number(cell.getAttribute('colspan'))
      const rowspan = Number(cell.getAttribute('rowspan'))
      const position = `r${rowIndex + 1}c${colIndex + 1}`

      if (colspan > 1) descriptions.push(`${position} spans ${colspan} cols`)
      if (rowspan > 1) descriptions.push(`${position} spans ${rowspan} rows`)
    })
  })

  return descriptions.length > 0 ? descriptions.join(', ') : null
}
